import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, formatDate, todayISO } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { F } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Check, X, HandCoins, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { employee_id: "", project_id: "", tanggal_dibutuhkan: todayISO(), nominal: 0, keperluan: "", jumlah_cicilan: 1, keterangan: "" };

export default function KasbonRequests() {
  const { user } = useAuth();
  const canApprove = ["super_admin", "finance", "direktur"].includes(user.role);
  const canSubmit = ["super_admin", "site_admin", "finance", "direktur", "hr"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [decideFor, setDecideFor] = useState(null);
  const [decNominal, setDecNominal] = useState(0);
  const [decCatatan, setDecCatatan] = useState("");
  const [del, setDel] = useState(null);

  const load = () => api.get("/kasbon-requests").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/employees?menu=/kasbon-requests").then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);
  const empName = (id, fallback) => employees.find((e) => e.id === id)?.nama || fallback || "-";

  const save = async () => {
    if (!form.employee_id || !form.nominal || !form.keperluan) return toast.error("Pegawai, nominal, & keperluan wajib diisi");
    try {
      await api.post("/kasbon-requests", { ...form, nominal: Number(form.nominal), jumlah_cicilan: Number(form.jumlah_cicilan) || 1 });
      toast.success("Pengajuan kasbon terkirim"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const decide = async (keputusan) => {
    try {
      await api.post(`/kasbon-requests/${decideFor.id}/decision`, {
        keputusan, nominal_disetujui: Number(decNominal), catatan: decCatatan,
      });
      toast.success(`Pengajuan ${keputusan.toLowerCase()}`); setDecideFor(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const makeKasbon = async (r) => {
    try { await api.post(`/kasbon-requests/${r.id}/create-kasbon`); toast.success("Kasbon dibuat dari pengajuan"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/kasbon-requests/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openDecide = (r) => { setDecideFor(r); setDecNominal(r.nominal); setDecCatatan(""); };

  const columns = [
    { key: "nomor", label: "No", sortable: true },
    { key: "employee_nama", label: "Pegawai", sortable: true, render: (r) => empName(r.employee_id, r.employee_nama) },
    { key: "nominal", label: "Diajukan", align: "right", render: (r) => rupiah(r.nominal) },
    { key: "nominal_disetujui", label: "Disetujui", align: "right", render: (r) => r.status === "Menunggu" ? <span className="text-slate-300">—</span> : <span className="font-semibold text-emerald-700">{rupiah(r.nominal_disetujui)}</span> },
    { key: "jumlah_cicilan", label: "Cicilan", align: "center", render: (r) => `${r.jumlah_cicilan}x` },
    { key: "keperluan", label: "Keperluan", render: (r) => <span className="text-sm">{r.keperluan}</span> },
    { key: "diajukan_oleh", label: "Diajukan oleh", render: (r) => <span className="text-xs text-slate-500">{r.diajukan_oleh}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    {
      key: "act", label: "Aksi", render: (r) => (
        <div className="flex flex-wrap gap-1">
          {canApprove && r.status === "Menunggu" && (
            <Button size="sm" variant="outline" data-testid={`kr-decide-${r.id}`} onClick={() => openDecide(r)}>Proses</Button>
          )}
          {canApprove && (r.status === "Disetujui" || r.status === "Disetujui Sebagian") && !r.kasbon_id && (
            <Button size="sm" className="bg-blue-800 hover:bg-blue-900" data-testid={`kr-make-${r.id}`} onClick={() => makeKasbon(r)}><HandCoins className="h-4 w-4 mr-1" /> Buat Kasbon</Button>
          )}
          {r.kasbon_id && <span className="text-xs text-emerald-600 font-semibold self-center">Kasbon dibuat</span>}
          {(canApprove || (r.diajukan_oleh_id === user.id && r.status === "Menunggu")) && (
            <Button size="icon" variant="ghost" className="text-red-600" data-testid={`kr-del-${r.id}`} onClick={() => setDel(r)}><Trash2 className="h-4 w-4" /></Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Pengajuan Kasbon" subtitle="Mandor mengajukan kasbon pegawai, Finance menyetujui / menolak">
        {canSubmit && <Button className="bg-blue-800 hover:bg-blue-900" data-testid="kr-add-btn" onClick={() => { setForm(emptyForm); setOpen(true); }}><Plus className="h-4 w-4 mr-1" /> Ajukan Kasbon</Button>}
      </PageHeader>
      <DataTable columns={columns} data={rows} searchKeys={["nomor", "employee_nama", "keperluan"]} testid="kr-table" />

      {/* Form Pengajuan */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Ajukan Kasbon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Nama Karyawan *">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="kr-f-emp"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.nama}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Proyek">
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="kr-f-proj"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Tanggal Dibutuhkan"><Input type="date" data-testid="kr-f-tgl" value={form.tanggal_dibutuhkan} onChange={(e) => setForm({ ...form, tanggal_dibutuhkan: e.target.value })} /></F>
              <F label="Jumlah Cicilan (x)"><Input type="number" min="1" data-testid="kr-f-cicilan" value={form.jumlah_cicilan} onChange={(e) => setForm({ ...form, jumlah_cicilan: e.target.value })} /></F>
            </div>
            <F label="Nominal Diajukan *"><Input type="number" data-testid="kr-f-nominal" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} /></F>
            <F label="Keperluan *"><Input data-testid="kr-f-keperluan" placeholder="mis. biaya berobat, pendidikan" value={form.keperluan} onChange={(e) => setForm({ ...form, keperluan: e.target.value })} /></F>
            <F label="Keterangan"><Textarea data-testid="kr-f-ket" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="kr-save-btn">Kirim Pengajuan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Keputusan */}
      <Dialog open={!!decideFor} onOpenChange={() => setDecideFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Proses Pengajuan {decideFor?.nomor}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Karyawan</span><span className="font-semibold">{empName(decideFor?.employee_id, decideFor?.employee_nama)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Diajukan</span><span className="font-mono font-semibold">{rupiah(decideFor?.nominal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Cicilan</span><span>{decideFor?.jumlah_cicilan}x</span></div>
              <div className="mt-1 text-slate-500">Keperluan: <span className="text-slate-800">{decideFor?.keperluan}</span></div>
            </div>
            <F label="Nominal Disetujui (isi < diajukan untuk setuju sebagian)">
              <Input type="number" data-testid="kr-dec-nominal" value={decNominal} onChange={(e) => setDecNominal(e.target.value)} />
            </F>
            <F label="Catatan"><Textarea data-testid="kr-dec-catatan" value={decCatatan} onChange={(e) => setDecCatatan(e.target.value)} /></F>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="text-red-600 border-red-200" data-testid="kr-dec-reject" onClick={() => decide("Ditolak")}><X className="h-4 w-4 mr-1" /> Tolak</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="kr-dec-partial" onClick={() => decide("Disetujui Sebagian")}>Setujui Sebagian</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="kr-dec-approve" onClick={() => decide("Disetujui")}><Check className="h-4 w-4 mr-1" /> Setujui Penuh</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} destructive testid="kr-del-dialog"
        title="Hapus Pengajuan?" description="Pengajuan kasbon ini akan dihapus." confirmText="Hapus" onConfirm={doDelete} />
    </div>
  );
}
